/* ═══════════════════════════════════════════════════════════════════════════
   Statusi naloga — ekran posrednika.

   ── STATUSI SE VODE PO RADNOM NALOGU ──────────────────────────────────────
   Jedna tura nosi više radnih naloga: više klijenata, više relacija, u istom
   kamionu. Nalogodavac je klijent JEDNOG radnog naloga i sme da vidi samo
   njegov put — zato se prvo bira radni nalog, pa se za njega izdaju linkovi i
   vode statusi. Vezano za turu, klijent bi video i tuđu robu iz istog kamiona.

   ── TURA I AGENCIJSKI NALOG NISU ISTO ─────────────────────────────────────
   Na TURAMA vozi naše vozilo i statuse upisuje operater aplikacije — spoljnog
   prevoznika nema, pa nema ni linka za njega. Taj link postoji SAMO na
   agencijskim nalozima (`props.agencijski`), gde turu izvršava podizvođač.
   Link za nalogodavca postoji u oba slučaja, jer klijent postoji uvek.

   ── ZAŠTO JE EKRAN OVAKO SLOŽEN ───────────────────────────────────────────
   Dispečer ne čita tabelu, nego traži odgovor na tri pitanja, u ovom redu:
   gde je roba sada, čeka li neko mene, i jesu li linkovi izdati. Zato je gore
   „Sada" (status i KOLIKO JE PROŠLO, jedini broj po kome se procenjuje da li
   nešto škripi), pored toga sandučić zahteva, pa koraci puta.

   Koraci razlikuju **preskočen** od budućeg: korak pre trenutnog bez prijave
   znači da ga prevoznik nije javio, i to je signal, ne praznina. Klik na korak
   bira taj status — koraci i padajući spisak su jedno isto stanje, pa se izbor
   vidi na oba mesta.

   ── ZAŠTO SE LINK VIDI SAMO JEDNOM ────────────────────────────────────────
   U bazi stoji samo SHA-256 otisak tokena, isto kao lozinka. Sam link postoji
   jedino u odgovoru na izdavanje i u mejlu koji odatle pošaljemo. Ko izgubi
   link izdaje nov — tada stari prestaje da važi, što je i jedini način da se
   prosleđen link povuče.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import './Ture.css';
import './statusiNaloga.css';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
import { AuthContext } from '../../Header/auth - context';
import { ChatSocketContext } from '../../context/ChatSocketContext';
import { uzmiGresku } from '../../custom/greske';
/* Birač lokacije na OpenStreetMap-u već postoji u chatu (trenutna lokacija +
   klik na mapu). Nema razloga za drugi — isti posao, isti izgled. */
import LocationPickerModal from '../Chat/LocationPickerModal';

/* 4 = naš vozač sa telefona (TMS Mobile), kad tura ide našim vozilom. */
const ROLA = { NALOGODAVAC: 1, PREVOZNIK: 2, POSREDNIK: 3, VOZAC: 4 };

/* Istorija se skuplja: dispečeru trebaju zadnja dva-tri koraka, a ne ceo put. */
const ISTORIJA_SKUPLJENO = 4;

const StatusiNaloga = (props) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);

    /* Na turama nema spoljnog prevoznika — vozi naše vozilo, statuse upisuje
       operater. Link za prevoznika zato postoji samo na agencijskim nalozima. */
    const agencijski = !!props.agencijski;

    const trip = useSelector(state => state.trip);

    const [radniNalozi, setRadniNalozi] = useState([]);
    const [izabran, setIzabran] = useState(null);
    const [nalog, setNalog] = useState({});
    const [sifarnik, setSifarnik] = useState([]);
    const [linkovi, setLinkovi] = useState([]);
    const [statusi, setStatusi] = useState([]);
    const [zahtevi, setZahtevi] = useState([]);

    /* Izdati link živi SAMO u stanju ovog ekrana — nema ga ni u bazi. */
    const [sveziLink, setSveziLink] = useState({});
    const [mejlovi, setMejlovi] = useState({});

    const [noviStatus, setNoviStatus] = useState(null);
    const [novaNapomena, setNovaNapomena] = useState('');
    const [koord, setKoord] = useState(null);
    const [biracLokacije, setBiracLokacije] = useState(false);
    const [radi, setRadi] = useState(false);
    const [sveIstorija, setSveIstorija] = useState(false);

    /* „pre 2 h 14 min" bi inače stajalo zamrznuto dok se ekran ne osveži. */
    const [otkucaj, setOtkucaj] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setOtkucaj(x => x + 1), 60000);
        return () => clearInterval(id);
    }, []);

    const zaglavlja = useCallback(() => ({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + auth.token,
    }), [auth.token]);

    const javi = (severity, detail) => toast.current?.show({
        severity,
        summary: severity === 'error' ? t('common.greska') : t('common.uspeh'),
        detail, life: 4000,
    });

    const naGresku = useCallback((e) => uzmiGresku(e, t('common.greska'))
        .then(m => toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: m.message, life: 5000 })),
        [t]);

    const zovi = useCallback((putanja, telo) =>
        fetch(process.env.REACT_APP_BACKEND_URL + '/pracenje' + putanja, {
            method: 'POST', headers: zaglavlja(), body: JSON.stringify(telo),
        }).then(r => (r.ok ? r.json() : Promise.reject(r))), [zaglavlja]);

    const ucitaj = useCallback((idRadni) => {
        if (!trip) { setRadniNalozi([]); setLinkovi([]); setStatusi([]); setZahtevi([]); return; }
        zovi('/', { id_putni_nalog: trip, id_radni_nalog: idRadni ?? null })
            .then(d => {
                if (d?.message) { javi('warn', d.message); return; }
                const spisak = d.RadniNalozi || [];
                setRadniNalozi(spisak);
                setSifarnik(d.Sifarnik || []);
                setNalog(d.Nalog || {});
                setLinkovi(d.Linkovi || []);
                setStatusi(d.Statusi || []);
                setZahtevi(d.Zahtevi || []);

                /* Jedan radni nalog = nema šta da se bira; ekran se otvara na njemu. */
                if (!idRadni && spisak.length === 1) {
                    setIzabran(spisak[0].id);
                    ucitaj(spisak[0].id);
                }
            })
            .catch(naGresku);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trip, zovi, naGresku]);

    useEffect(() => { setIzabran(null); ucitaj(null); }, [ucitaj]);

    /* Veza sa telefonom: kad vozač (ili prevoznik sa linka, ili kolega) upiše
       status, server javi celoj firmi 'nalog-status'. Ako je taj radni nalog na
       ovoj turi, ekran se osveži sam — bez ručnog osvežavanja stranice. Ref-ovi
       jer se listener kači jednom po socketu, a spisak i izbor se menjaju. */
    const { socket } = useContext(ChatSocketContext) || {};
    const nalogeRef = useRef([]);
    const izabranRef = useRef(null);
    useEffect(() => { nalogeRef.current = radniNalozi.map(r => r.id); }, [radniNalozi]);
    useEffect(() => { izabranRef.current = izabran; }, [izabran]);
    useEffect(() => {
        if (!socket) return undefined;
        const naPromenu = ({ id_radni_nalog }) => {
            if (!nalogeRef.current.includes(id_radni_nalog)) return;
            ucitaj(izabranRef.current);
        };
        socket.on('nalog-status', naPromenu);
        return () => socket.off('nalog-status', naPromenu);
    }, [socket, ucitaj]);

    /* Pri promeni ture ili radnog naloga se izdati link mora zabaciti — inače
       bi ostao na ekranu pored podataka drugog naloga i neko bi ga poslao
       pogrešnoj strani. */
    useEffect(() => {
        setSveziLink({}); setMejlovi({}); setSveIstorija(false);
        setNoviStatus(null); setNovaNapomena(''); setKoord(null);
    }, [trip, izabran]);

    const izaberi = (id) => {
        const novi = izabran === id ? null : id;
        setIzabran(novi);
        ucitaj(novi);
    };

    /* ── izvedeno ──────────────────────────────────────────────────────── */

    const proteklo = useCallback((iso) => {
        if (!iso) return '';
        const tada = new Date(iso);
        if (Number.isNaN(tada.getTime())) return '';
        const sek = Math.max(0, (Date.now() - tada.getTime()) / 1000);
        if (sek < 60) return t('statusiNaloga.upravo');
        const min = Math.floor(sek / 60);
        const sati = Math.floor(min / 60);
        const dana = Math.floor(sati / 24);
        /* Jedinice ostaju skraćenice (d, h, min) — u svim jezicima se čitaju
           isto, pa ne traže prevod, a red veličine se vidi odmah. */
        const tekst = dana > 0 ? `${dana} d ${sati % 24} h`
            : sati > 0 ? `${sati} h ${min % 60} min`
            : `${min} min`;
        return t('statusiNaloga.pre', { vreme: tekst });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, otkucaj]);

    /* ── BOJA STATUSA ───────────────────────────────────────────────────────
       Boja se računa iz MESTA u lancu, ne iz natpisa i ne iz šifre.

       Natpis se prevodi na sedam jezika, a šifra se menja pri prenumerisanju
       šifarnika (grupa 20 je prenumerisana 15.10.2026, vidi modgen.sql) — oba
       bi odvela u pogrešnu boju. Mesto u lancu je jedino što ostaje.

       Pet faza, pa dispečer po boji zna fazu bez čitanja:
         početak   siva        — nalog postoji, roba se nije pomerila
         utovar    žuta        — roba se preuzima, još nije krenula
         put       plava       — roba se kreće (tranzit, granica, carine)
         istovar   ljubičasta  — roba je na odredištu, predaja u toku
         kraj      zelena      — istovarena, posao završen

       Granice su u DELOVIMA lanca (trećine), ne u brojevima koraka, pa i
       skraćen ili produžen lanac daje smislene boje. */
    const faza = useCallback((statusId) => {
        if (statusId == null || !sifarnik.length) return 'nepoznat';
        const i = sifarnik.findIndex(x => Number(x.value) === Number(statusId));
        if (i < 0) return 'nepoznat';
        if (i === 0) return 'pocetak';
        if (i === sifarnik.length - 1) return 'kraj';
        const deo = i / (sifarnik.length - 1);
        return deo < 0.34 ? 'utovar' : deo < 0.67 ? 'put' : 'istovar';
    }, [sifarnik]);

    /* Koliko je lanca prešlo — crta se kao tanka linija na kartici. */
    const mera = useCallback((statusId) => {
        if (statusId == null || sifarnik.length < 2) return 0;
        const i = sifarnik.findIndex(x => Number(x.value) === Number(statusId));
        return i < 0 ? 0 : Math.round(((i + 1) / sifarnik.length) * 100);
    }, [sifarnik]);

    /* Lista dolazi poređana po vremenu opadajuće, pa je prvi red trenutni status. */
    const trenutni = statusi[0] || null;
    const nereseni = useMemo(() => zahtevi.filter(z => !z.resen), [zahtevi]);

    /* Koraci puta. Šifarnik je poređan po redosledu puta (id raste), pa je
       mesto u šifarniku i mesto na putu. */
    const koraci = useMemo(() => {
        const prijavljeni = new Set(statusi.map(s => Number(s.statusNaloga)));
        const sada = trenutni ? sifarnik.findIndex(x => Number(x.value) === Number(trenutni.statusNaloga)) : -1;
        return sifarnik.map((x, i) => ({
            ...x,
            redni: i + 1,
            stanje: i === sada ? 'sada'
                : i < sada ? (prijavljeni.has(Number(x.value)) ? 'presao' : 'preskocen')
                : prijavljeni.has(Number(x.value)) ? 'presao' : 'ispred',
        }));
    }, [sifarnik, statusi, trenutni]);

    const indeksSada = koraci.findIndex(k => k.stanje === 'sada');
    const dosegnut = indeksSada + 1;
    const procenat = koraci.length ? Math.round((dosegnut / koraci.length) * 100) : 0;
    const sledeci = indeksSada >= 0 ? koraci[indeksSada + 1] : koraci[0];

    const vidljivaIstorija = sveIstorija ? statusi : statusi.slice(0, ISTORIJA_SKUPLJENO);

    const nalogZa = (rola) => linkovi.find(l => Number(l.rola) === rola) || null;

    const imeRole = (rola) => Number(rola) === ROLA.NALOGODAVAC ? t('pracenje.rolaNalogodavac')
        : Number(rola) === ROLA.PREVOZNIK ? t('pracenje.rolaPrevoznik')
        : Number(rola) === ROLA.VOZAC ? t('pracenje.rolaVozac')
        : t('pracenje.rolaPosrednik');

    const klasaRole = (rola) => Number(rola) === ROLA.NALOGODAVAC ? 'nalogodavac'
        : Number(rola) === ROLA.PREVOZNIK ? 'prevoznik'
        : Number(rola) === ROLA.VOZAC ? 'vozac' : 'posrednik';

    /* ── radnje ────────────────────────────────────────────────────────── */

    /* Nalogodavac JE klijent tog radnog naloga, pa se njegova adresa sama
       predlaže — bez toga se traži po klijentima za svaki link. */
    const podrazumevanaAdresa = (rola) =>
        rola === ROLA.NALOGODAVAC ? (nalog.klijentEmail || '') : '';

    const adresaZa = (rola) => mejlovi[rola] ?? podrazumevanaAdresa(rola);

    const izdaj = (rola) => {
        if (!izabran) return;
        setRadi(true);
        const adrese = String(adresaZa(rola)).split(',').map(x => x.trim()).filter(Boolean);
        zovi('/izdajLink', {
            id_radni_nalog: izabran, rola, emails: adrese,
            /* Broj i relacija idu u naslov i telo mejla — bez njih primalac
               dobije poruku bez naznake o kom nalogu se radi. */
            brojNaloga: nalog.brojNaloga || '', relacija: nalog.relacija || '',
        })
            .then(d => {
                if (!d?.link) { javi('warn', d?.message || t('common.greska')); return; }
                setSveziLink(prev => ({ ...prev, [rola]: d.link }));
                if (d.poslato?.length) javi('success', `${t('statusiNaloga.poslatoNa')}: ${d.poslato.join(', ')}`);
                if (d.neuspelo?.length) javi('warn', `${t('statusiNaloga.mejlNijePoslat')}: ${d.neuspelo.join(', ')}`);
                if (!adrese.length) javi('success', d.message);
                ucitaj(izabran);
            })
            .catch(naGresku)
            .finally(() => setRadi(false));
    };

    const ugasi = (link) => confirmDialog({
        message: t('statusiNaloga.potvrdaUgasi'),
        acceptLabel: t('common.da'), rejectLabel: t('common.ne'),
        icon: 'pi pi-exclamation-triangle', acceptClassName: 'p-button-danger',
        accept: () => zovi('/ugasiLink', { id: link.id })
            .then(d => { javi('success', d?.message || t('common.sacuvano')); setSveziLink({}); ucitaj(izabran); })
            .catch(naGresku),
    });

    const rezerva = (tekst, uspeh) => {
        const polje = document.createElement('textarea');
        polje.value = tekst;
        polje.style.position = 'fixed';
        polje.style.opacity = '0';
        document.body.appendChild(polje);
        polje.select();
        try { document.execCommand('copy'); uspeh(); } catch { /* bez poruke je bolje nego lažna */ }
        document.body.removeChild(polje);
    };

    const kopiraj = (rola) => {
        const link = sveziLink[rola];
        if (!link) return;
        /* `navigator.clipboard` ne postoji na HTTP-u u starijim pregledačima,
           pa ide i rezerva preko skrivenog polja — inače dugme tiho ne radi. */
        const uspeh = () => javi('success', t('statusiNaloga.kopirano'));
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(link).then(uspeh).catch(() => rezerva(link, uspeh));
        } else rezerva(link, uspeh);
    };

    const snimiStatus = (statusZaUpis, id_zahtev = null) => {
        const status = statusZaUpis ?? noviStatus;
        if (!izabran) return;
        if (!status) { javi('warn', t('pracenje.obavezanStatus')); return; }
        setRadi(true);
        zovi('/snimiStatus', {
            id_radni_nalog: izabran, statusNaloga: status,
            napomena: novaNapomena,
            lat: koord?.lat ?? null, lon: koord?.lon ?? null,
            id_zahtev,
        })
            .then(d => {
                javi('success', d?.message || t('common.sacuvano'));
                setNoviStatus(null); setNovaNapomena(''); setKoord(null);
                ucitaj(izabran);
            })
            .catch(naGresku)
            .finally(() => setRadi(false));
    };

    const resi = (id) => zovi('/resiZahtev', { id })
        .then(d => { javi('success', d?.message || t('common.sacuvano')); ucitaj(izabran); })
        .catch(naGresku);

    /* ── delovi ekrana ─────────────────────────────────────────────────── */

    const lokacijaReda = (s) => {
        if (!s.lokacija && (s.lat == null || s.lon == null)) return null;
        return (
            <span className="sn-mesto">
                <i className="pi pi-map-marker" />
                {s.lokacija || `${s.lat}, ${s.lon}`}
                {s.lat != null && s.lon != null && (
                    <a href={`https://www.google.com/maps?q=${s.lat},${s.lon}`}
                       target="_blank" rel="noreferrer">{t('pracenje.otvoriKartu')}</a>
                )}
            </span>
        );
    };

    const propusnica = (rola, naslov, ikona) => {
        const link = nalogZa(rola);
        const svez = sveziLink[rola];
        const stanje = !link ? 'nema' : link.zakljucan ? 'zakljucan' : 'aktivan';
        const adresa = adresaZa(rola);
        const saMejlom = String(adresa).trim() !== '';

        return (
            <div className={`sn-propusnica stanje-${stanje} rola-${klasaRole(rola)}`}>
                <div className="sn-prop-glava">
                    <span className="sn-prop-ikona"><i className={`pi ${ikona}`} /></span>
                    <div className="sn-prop-ime">
                        <span className="sn-mikro">{imeRole(rola)}</span>
                        <strong>{naslov}</strong>
                    </div>
                    <span className={`sn-pilula ${stanje}`}>
                        {stanje === 'nema' ? t('statusiNaloga.nemaLinka')
                            : stanje === 'zakljucan' ? t('statusiNaloga.zakljucan')
                            : t('statusiNaloga.linkPostoji')}
                    </span>
                </div>

                {link && (
                    <div className="sn-prop-info">
                        {t('statusiNaloga.izdatDana')} {link.izdat}
                        {link.vaziDo ? ` · ${t('pracenje.vaziDo')} ${link.vaziDo}` : ''}
                    </div>
                )}

                <div className="sn-prop-mejl">
                    <span className="p-input-icon-left">
                        <i className="pi pi-envelope" />
                        <InputText className="p-inputtext-sm" value={adresa}
                                   placeholder={t('statusiNaloga.mejlovi')}
                                   onChange={e => setMejlovi(prev => ({ ...prev, [rola]: e.target.value }))} />
                    </span>
                </div>

                {/* Jedna glavna radnja, ostalo su tihe ikonice: dugme samo kaže
                    šta će se desiti sa unetim adresama, pa nema dva dugmeta koja
                    rade skoro isto. */}
                <div className="sn-akcije">
                    <button type="button" className={`sn-glavna ${saMejlom ? 'posalji' : ''}`}
                            disabled={radi || !izabran} onClick={() => izdaj(rola)}>
                        <i className={`pi ${saMejlom ? 'pi-send' : link ? 'pi-refresh' : 'pi-link'}`} />
                        <span>
                            {saMejlom ? t('statusiNaloga.posaljiMejlom')
                                : link ? t('statusiNaloga.obnovi') : t('statusiNaloga.izdaj')}
                        </span>
                    </button>

                    {svez && (
                        <button type="button" className="sn-ikona sn-kopiraj" onClick={() => kopiraj(rola)}
                                data-pr-tooltip={t('ruta.kopirajLink')}>
                            <i className="pi pi-copy" />
                        </button>
                    )}

                    {link && (
                        <button type="button" className="sn-ikona sn-opasno" onClick={() => ugasi(link)}
                                data-pr-tooltip={t('statusiNaloga.ugasi')}>
                            <i className="pi pi-ban" />
                        </button>
                    )}
                </div>

                {svez && (
                    <div className="sn-karta">
                        <div className="sn-karta-link">{svez}</div>
                        <p className="sn-karta-upozorenje">
                            <i className="pi pi-shield" /> {t('statusiNaloga.linkJednom')}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    if (!trip) {
        return (
            <div className="col-12 sn-koren">
                <Toast ref={toast} />
                <div className="sn-panel sn-pusto">
                    <i className="pi pi-map" />
                    <p>{t('statusiNaloga.izaberiNalog')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="col-12 sn-koren">
            <Toast ref={toast} />
            <Tooltip target=".sn-ikona" position="top" />

            {biracLokacije && (
                <LocationPickerModal
                    onClose={() => setBiracLokacije(false)}
                    onConfirm={(lat, lon) => {
                        setKoord({ lat: Number(lat.toFixed(6)), lon: Number(lon.toFixed(6)) });
                        setBiracLokacije(false);
                    }}
                />
            )}

            {/* ── birač radnog naloga ── */}
            <div className="sn-panel">
                <div className="sn-mikro-red">
                    <span className="sn-mikro">{t('statusiNaloga.radniNalozi')}</span>
                    <span className="sn-tiho sn-nagovestaj">
                        <i className="pi pi-info-circle" /> {t('statusiNaloga.izaberiRadni')}
                    </span>
                </div>

                {radniNalozi.length === 0 && <p className="sn-tiho">{t('statusiNaloga.nemaRadnih')}</p>}

                {/* Vodoravna traka, ne mreža: tura sa deset naloga je u mreži
                    gurala sve ostalo (status, sandučić, korake) van ekrana, a
                    dispečeru prvo treba ono ISPOD izbora. Ovako birač ima
                    stalnu visinu od tri reda, koliko god naloga bilo. */}
                <div className="sn-karte">
                    {radniNalozi.map(r => (
                        <button type="button" key={r.id}
                                className={`sn-kartica faza-${faza(r.statusId)} ${izabran === r.id ? 'izabrana' : ''}`}
                                onClick={() => izaberi(r.id)}>
                            {/* Mera napretka kao linija uz gornju ivicu — koliko je
                                puta prešlo vidi se bez čitanja teksta. */}
                            <span className="sn-kartica-mera" aria-hidden="true">
                                <span style={{ width: `${mera(r.statusId)}%` }} />
                            </span>

                            <div className="sn-kartica-glava">
                                <strong>{r.broj}</strong>
                                <span className="sn-kartica-znaci">
                                    {r.zahteva > 0 && (
                                        <span className="sn-tocka pazi" title={t('statusiNaloga.cekaOdgovor')}>
                                            {r.zahteva}
                                        </span>
                                    )}
                                    <span className={`sn-tocka ${r.linkova > 0 ? 'ok' : ''}`}
                                          title={t('statusiNaloga.linkovi')}>
                                        <i className="pi pi-link" />
                                    </span>
                                </span>
                            </div>

                            <div className="sn-kartica-stanje">
                                <span className="sn-znacka">
                                    <span className="sn-znacka-tacka" />
                                    {r.status || t('statusiNaloga.bezStatusa')}
                                </span>
                                {r.vremeIso && <span className="sn-tiho">{proteklo(r.vremeIso)}</span>}
                            </div>

                            {/* Klijent i relacija u JEDNOM redu: dva reda su karticu
                                činila visokom, a relacija je i pre bila skraćena. */}
                            <div className="sn-kartica-ko"
                                 title={[r.klijent, r.relacija].filter(Boolean).join(' · ')}>
                                {r.klijent && <span className="sn-kartica-klijent">{r.klijent}</span>}
                                {r.klijent && r.relacija && <span className="sn-razdvoj">·</span>}
                                {r.relacija && <span className="sn-kartica-relacija">{r.relacija}</span>}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {!izabran ? null : (
            <>
                {/* ── zaglavlje izabranog naloga ── */}
                <div className="sn-traka">
                    <div className="sn-traka-levo">
                        <span className="sn-mikro">{t('statusiNaloga.naslov')}</span>
                        <h2>{nalog.brojNaloga || '—'}</h2>
                        {nalog.relacija && <div className="sn-relacija">{nalog.relacija}</div>}
                    </div>
                    <div className="sn-traka-desno">
                        {nalog.klijent && (
                            <span className="sn-cip"><i className="pi pi-briefcase" /> {nalog.klijent}</span>
                        )}
                        <span className={`sn-cip ${nereseni.length ? 'pazi' : ''}`}>
                            <i className="pi pi-inbox" /> {nereseni.length}
                        </span>
                        <span className="sn-cip">
                            <i className="pi pi-link" /> {linkovi.filter(l => l.aktivan).length}/{agencijski ? 2 : 1}
                        </span>
                    </div>
                </div>

                {/* ── sada + sandučić ── */}
                <div className="sn-mreza">
                    {/* Ista boja faze kao na kartici — izbor i detalj se ne smeju
                        razlikovati u boji, inače boja prestaje da znači fazu. */}
                    <div className={`sn-panel sn-sada faza-${faza(trenutni?.statusNaloga)} ${trenutni ? '' : 'prazno'}`}>
                        <span className="sn-mikro">{t('statusiNaloga.sada')}</span>
                        {trenutni ? (
                            <>
                                <div className="sn-sada-glava">
                                    <span className={`sn-puls rola-${klasaRole(trenutni.prijavioRola)}`} />
                                    <h3>{trenutni.status}</h3>
                                </div>
                                <div className="sn-sada-vreme">
                                    <strong>{proteklo(trenutni.vremeIso)}</strong>
                                    <span>{trenutni.vremeStatusa}</span>
                                </div>
                                <div className="sn-sada-red">
                                    <span className={`sn-znak rola-${klasaRole(trenutni.prijavioRola)}`}>
                                        {imeRole(trenutni.prijavioRola)}
                                    </span>
                                    {lokacijaReda(trenutni)}
                                </div>
                                {trenutni.napomena && <p className="sn-citat">{trenutni.napomena}</p>}
                            </>
                        ) : (
                            <p className="sn-tiho">{t('statusiNaloga.nijePocelo')}</p>
                        )}
                    </div>

                    <div className={`sn-panel sn-sanduce ${nereseni.length ? 'pazi' : ''}`}>
                        <span className="sn-mikro">{t('pracenje.zahtevi')}</span>
                        {nereseni.length === 0 && <p className="sn-tiho">{t('pracenje.nemaZahteva')}</p>}
                        {nereseni.slice(0, 2).map(z => (
                            <div key={z.id} className="sn-zahtev">
                                <div className="sn-zahtev-glava">
                                    <span className="sn-pilula pazi">{t('statusiNaloga.cekaOdgovor')}</span>
                                    <span className="sn-tiho">{z.datum}</span>
                                </div>
                                {z.trazeniStatusOpis && <div className="sn-traze">{z.trazeniStatusOpis}</div>}
                                {z.napomena && <p className="sn-citat">{z.napomena}</p>}
                                <div className="sn-zahtev-akcije">
                                    {z.trazeniStatus && (
                                        <Button type="button" icon="pi pi-check-circle" severity="success" size="small"
                                                label={t('pracenje.potvrdiZahtev')} disabled={radi}
                                                onClick={() => snimiStatus(z.trazeniStatus, z.id)} />
                                    )}
                                    <Button type="button" icon="pi pi-flag" severity="secondary" text size="small"
                                            label={t('statusiNaloga.resi')} onClick={() => resi(z.id)} />
                                </div>
                            </div>
                        ))}
                        {nereseni.length > 2 && (
                            <div className="sn-tiho sn-vise">+{nereseni.length - 2}</div>
                        )}
                    </div>
                </div>

                {/* ── koraci puta + unos ── */}
                <div className={`sn-panel sn-napredak faza-${faza(trenutni?.statusNaloga)}`}>
                    <div className="sn-mikro-red">
                        <span className="sn-mikro">{t('statusiNaloga.napredak')}</span>
                        {dosegnut > 0 && (
                            <span className="sn-brojac">
                                {dosegnut} / {koraci.length}
                                <em>{procenat}%</em>
                            </span>
                        )}
                    </div>

                    {/* Mera napretka stoji kao traka IZNAD koraka: koliko je puta
                        prešlo čita se iz jedne linije, bez prebrojavanja tačaka. */}
                    <div className="sn-mera" role="presentation">
                        <span className="sn-mera-pun" style={{ width: `${procenat}%` }} />
                    </div>

                    {/* Klik na korak BIRA taj status. Koraci i padajući spisak dele
                        isto stanje (`noviStatus`), pa se izbor vidi na oba mesta —
                        inače bi dva mesta pokazivala dve različite istine. */}
                    <div className="sn-sine" role="group" aria-label={t('statusiNaloga.napredak')}>
                        {koraci.map(k => (
                            <button type="button" key={k.value}
                                    className={`sn-korak faza-${faza(k.value)} ${k.stanje} ${Number(noviStatus) === Number(k.value) ? 'biran' : ''}`}
                                    aria-pressed={Number(noviStatus) === Number(k.value)}
                                    onClick={() => setNoviStatus(Number(noviStatus) === Number(k.value) ? null : k.value)}
                                    title={k.stanje === 'preskocen' ? `${k.label} — ${t('statusiNaloga.preskocen')}` : k.label}>
                                <span className="sn-veza" />
                                <span className="sn-tacka">
                                    {k.stanje === 'presao' ? <i className="pi pi-check" />
                                        : k.stanje === 'preskocen' ? <i className="pi pi-exclamation-triangle" />
                                        : k.redni}
                                </span>
                                <span className="sn-korak-ime">{k.label}</span>
                            </button>
                        ))}
                    </div>

                    {sledeci && !noviStatus && (
                        <div className="sn-sledeci">
                            <span className="sn-mikro">{t('statusiNaloga.sledeci')}</span>
                            <strong>{sledeci.label}</strong>
                            <Button type="button" icon="pi pi-arrow-right" severity="success" size="small" outlined
                                    label={t('common.unos')} disabled={radi}
                                    onClick={() => snimiStatus(sledeci.value, null)} />
                        </div>
                    )}
                    {!sledeci && dosegnut > 0 && !noviStatus && (
                        <div className="sn-sledeci zavrseno">
                            <i className="pi pi-flag-fill" /> {t('statusiNaloga.zavrseno')}
                        </div>
                    )}

                    {/* Unos stoji ISPOD koraka, jer je isti posao: izbor je već
                        napravljen klikom na korak ili u spisku. */}
                    <div className="sn-unos">
                        <Dropdown value={noviStatus} options={sifarnik} className="p-inputtext-sm"
                                  onChange={e => setNoviStatus(e.value)} showClear
                                  placeholder={t('pracenje.izaberiStatus')} />

                        {/* Lokacija se bira na karti (OpenStreetMap), sa „moja
                            lokacija" ili klikom. Slobodan tekst je izbačen: dve
                            firme isto mesto otkucaju različito, a koordinata se
                            može otvoriti na karti. */}
                        {koord ? (
                            <span className="sn-koord-cip">
                                <i className="pi pi-map-marker" />
                                {koord.lat}, {koord.lon}
                                <button type="button" onClick={() => { setKoord(null); javi('info', t('statusiNaloga.lokacijaUklonjena')); }}
                                        aria-label={t('common.delete')}>
                                    <i className="pi pi-times" />
                                </button>
                            </span>
                        ) : (
                            <Button type="button" icon="pi pi-map" severity="secondary" outlined size="small"
                                    label={t('statusiNaloga.lokacijaSaKarte')}
                                    onClick={() => setBiracLokacije(true)} />
                        )}

                        <InputTextarea value={novaNapomena} rows={1} autoResize className="p-inputtext-sm"
                                       onChange={e => setNovaNapomena(e.target.value)}
                                       placeholder={t('nova.napomena')} />
                        <Button type="button" icon="pi pi-plus" severity="success" size="small" disabled={radi}
                                label={t('common.unos')} onClick={() => snimiStatus(null, null)} />
                    </div>
                    <p className="sn-tiho sn-mala">
                        <i className="pi pi-info-circle" /> {t('statusiNaloga.samoOperater')}
                    </p>
                </div>

                {/* ── linkovi ── */}
                <div className="sn-panel">
                    <div className="sn-mikro-red">
                        <span className="sn-mikro">{t('statusiNaloga.linkovi')}</span>
                        <span className="sn-tiho sn-nagovestaj">
                            <i className="pi pi-info-circle" /> {t('statusiNaloga.odobrenje')}
                        </span>
                    </div>
                    <div className={`sn-propusnice ${agencijski ? '' : 'jedna'}`}>
                        {propusnica(ROLA.NALOGODAVAC, t('statusiNaloga.linkNalogodavac'), 'pi-eye')}
                        {/* Samo agencijski nalog ima spoljnog prevoznika. Na turama
                            vozi naše vozilo, pa link za prevoznika ne postoji. */}
                        {agencijski && propusnica(ROLA.PREVOZNIK, t('statusiNaloga.linkPrevoznik'), 'pi-truck')}
                    </div>
                </div>

                {/* ── istorija ── */}
                <div className="sn-panel">
                    <div className="sn-mikro-red">
                        <span className="sn-mikro">{t('pracenje.istorija')}</span>
                        {statusi.length > ISTORIJA_SKUPLJENO && (
                            <Button type="button" text size="small"
                                    icon={sveIstorija ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                                    label={sveIstorija ? t('statusiNaloga.sakrij')
                                        : t('statusiNaloga.prikaziSve', { broj: statusi.length })}
                                    onClick={() => setSveIstorija(x => !x)} />
                        )}
                    </div>

                    {statusi.length === 0 && <p className="sn-tiho">{t('pracenje.nemaStatusa')}</p>}

                    <div className="sn-linija">
                        {vidljivaIstorija.map(s => (
                            <div key={s.id} className="sn-zapis">
                                <span className={`sn-zapis-tacka rola-${klasaRole(s.prijavioRola)}`} />
                                <div className="sn-zapis-telo">
                                    <div className="sn-zapis-glava">
                                        <strong>{s.status}</strong>
                                        <span className="sn-tiho">{s.vremeStatusa}</span>
                                        <span className="sn-tiho sn-proteklo">{proteklo(s.vremeIso)}</span>
                                        {s.menjan === 1 && (
                                            <span className="sn-pilula tiho">{t('statusiNaloga.menjano')}</span>
                                        )}
                                    </div>
                                    <div className="sn-zapis-meta">
                                        <span className={`sn-znak rola-${klasaRole(s.prijavioRola)}`}>
                                            {imeRole(s.prijavioRola)}{s.korisnik ? ` · ${s.korisnik}` : ''}
                                        </span>
                                        {lokacijaReda(s)}
                                    </div>
                                    {s.napomena && <p className="sn-citat">{s.napomena}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── rešeni zahtevi (dokaz da je odgovoreno) ── */}
                {zahtevi.some(z => z.resen) && (
                    <div className="sn-panel">
                        <span className="sn-mikro">{t('statusiNaloga.resen')}</span>
                        <div className="sn-reseni">
                            {zahtevi.filter(z => z.resen).map(z => (
                                <div key={z.id} className="sn-resen">
                                    <i className="pi pi-check-circle" />
                                    <div>
                                        <div>
                                            {z.trazeniStatusOpis || t('nova.napomena')}
                                            <span className="sn-tiho"> · {z.datum}</span>
                                        </div>
                                        {z.napomena && <p className="sn-citat">{z.napomena}</p>}
                                        {z.resio && (
                                            <div className="sn-tiho">{z.resio} · {z.datumResenja}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </>
            )}
        </div>
    );
};

export default StatusiNaloga;
